import {
  FormControl,
  FormLabel,
  IconButton,
  InputAdornment,
  TextField,
  TextFieldProps,
} from "@mui/material";
import { IQuestion } from "../../interfaces";
import { Close } from "@mui/icons-material";
import {nanoid} from "nanoid";
import {
  cpf,
  cellphone,
  rg,
  cep,
  formatDate,
  email,
  onlyNumber,
  validateCpf,
  rgRne
} from "../../utils";
import { Controller, useFormContext } from "react-hook-form";
import { useApp } from "../../Contexts/AppProvider";
import React from "react";
import { getDataCEP, getEstados } from "../../Service/api";

interface IProps extends IQuestion {
  propsInput?: TextFieldProps
}
export default function QuestionTextInput({
  index,
  question,
  type,
  options,
  fieldNameVoucher,
  mask,
  description,
  defaultValue,
  hasTree,
  propsInput
}: IProps) {
  const {onSetCurrentQuestions, onSetEstados, onSetCidades, onSetDisabledButtons, questions} = useApp();
  const { control, setValue, setError, clearErrors } = useFormContext()
  const keyInput = nanoid();

  React.useEffect(() => {
    onSetCurrentQuestions({
      index,
      question,
      type,
      options,
      fieldNameVoucher,
      mask,
      description,
      defaultValue,
      hasTree,
    });

    return () => {
      onSetCurrentQuestions({
        index,
        question,
        type,
        options,
        fieldNameVoucher,
        mask,
        description,
        defaultValue,
        hasTree,
      }, true);
    }
  }, [
    defaultValue,
    description,
    hasTree,
    index,
    mask,
    onSetCurrentQuestions,
    options,
    question,
    type,
    fieldNameVoucher
  ])

  return (
      <Controller
        name={index.toString()}
        control={control}
        defaultValue={""}
        render={({ field: {value, name, ref}, formState: {errors} }) => {
          return (
            <FormControl
              sx={{mb: 1.9, mt: 1.1}}
              fullWidth
              error={!!errors[index.toString()]?.message}
            >
            <FormLabel sx={{mb: 1}} id={`${keyInput}-TextInput-label`}>{question}</FormLabel>
              <TextField
                fullWidth
                required
                ref={ref}
                name={name}
                value={value}
                id={`${keyInput}-TextInput`}
                focused={!!errors[index.toString()]?.message}
                error={!!errors[index.toString()]?.message}
                helperText={description || errors[index.toString()]?.message as string}
                placeholder={
                  mask === 'cpf' ? '___.___.___-__'
                  : mask === 'phone' ? '(__) _____-____'
                  : mask === 'rg' ? '__.___.___-___'
                  : mask === 'cep' ? '_____-___'
                  : mask === 'date' ? '__/__/____'
                  : "digite aqui"
                }        
                type={type === 'string' ? 'text' : type}
                InputProps={{
                  endAdornment: mask === 'date' ?
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          setValue(index.toString(), '');
                          clearErrors(index.toString());
                        }}
                        disabled={!value}
                        edge="end"
                      >
                        <Close />
                      </IconButton>
                    </InputAdornment> : null
                }}
                onChange={async ({target: {value}}) => {
                  clearErrors(index.toString());
                  onSetDisabledButtons(false);
                  setValue(index.toString(), mask === 'cpf' ? cpf(value)
                  : mask === 'phone' ? cellphone(value)
                  : mask === 'rg' ? rg(value)
                  : mask === 'rgRne' ? rgRne(value)
                  : mask === 'cep' || mask === 'cep-autocomplete' ? cep(value)
                  : mask === 'date' ? formatDate(value)
                  : type === 'email' ? email(value)
                  : type === 'number' ? onlyNumber(value) 
                  : value);

                  if (mask === 'cep-autocomplete' && cep(value).length === 9) {
                    try {
                      onSetEstados({list: [], loading: true});
                      const estadosData = await getEstados();
                      onSetEstados({list: estadosData, loading: false});
                      const dataCep = await getDataCEP(onlyNumber(value));
                      if (dataCep?.erro) {
                        onSetDisabledButtons(true);
                        setError(index.toString(), {
                          type: "pattern",
                          message: 'Digite um CEP válido.',
                        })
                        return;
                      }
                      const nomeEstado = estadosData?.find(e => e?.sigla === dataCep?.uf)?.nome;
                      onSetCidades({list: [{nome: dataCep?.localidade}], loading: false});
                      onSetEstados({list: nomeEstado ? [{nome: nomeEstado}] : [], loading: false});

                      const tipoEnderecoRef = questions?.find(q => q.question_ref === 'tipo_endereco');
                      if (tipoEnderecoRef) {
                        setValue(tipoEnderecoRef.index, dataCep?.logradouro?.split(' ')[0]);
                      }

                      const enderecoRef = questions?.find(q => q.question_ref === 'endereco');
                      if (enderecoRef) {
                        setValue(enderecoRef.index, dataCep?.logradouro);
                      }

                      const estadoRef = questions?.find(q => q.type === 'estados');
                      if (estadoRef) {
                        setValue(estadoRef.index, nomeEstado || "-1");
                      }

                      const cidadeRef = questions?.find(q => q.type === 'cidades');
                      if (cidadeRef) {
                        setValue(cidadeRef.index, dataCep?.localidade || "-1");
                      }

                      const bairroRef = questions?.find(q => q.question_ref === 'bairro_endereco');
                      if (bairroRef) {
                        setValue(bairroRef.index, dataCep?.bairro);
                      }
                    } catch (_err) {
                      onSetDisabledButtons(true);
                      setError(index.toString(), {
                        type: "pattern",
                        message: 'Digite um CEP válido.',
                      })
                      return;
                    }
                  } else if (mask === 'cep-autocomplete' && cep(value).length === 0) {
                    const tipoEnderecoRef = questions?.find(q => q.question_ref === 'tipo_endereco');
                    if (tipoEnderecoRef) {
                      setValue(tipoEnderecoRef.index, "");
                    }

                    const enderecoRef = questions?.find(q => q.question_ref === 'endereco');
                    if (enderecoRef) {
                      setValue(enderecoRef.index, "");
                    }

                    const estadoRef = questions?.find(q => q.type === 'estados');
                    if (estadoRef) {
                      setValue(estadoRef.index, "-1");
                    }

                    const cidadeRef = questions?.find(q => q.type === 'cidades');
                    if (cidadeRef) {
                      setValue(cidadeRef.index, "-1");
                    }

                    const bairroRef = questions?.find(q => q.question_ref === 'bairro_endereco');
                    if (bairroRef) {
                      setValue(bairroRef.index, "");
                    }
                    onSetCidades({list: [], loading: false});
                    onSetEstados({list: [], loading: true});
                    const estadosData = await getEstados();
                    onSetEstados({list: estadosData, loading: false});
                  }
                }}
                onBlur={async ({target: {value}}) => {
                  if(value && mask === 'cpf' && !validateCpf(onlyNumber(value))){
                    onSetDisabledButtons(true);
                    setError(index.toString(), {
                      type: "pattern",
                      message: 'Digite um CPF válido.',
                    })
                  } else 
                  if(value && mask === 'date' && value.length < 10){
                    onSetDisabledButtons(true);
                    setError(index.toString(), {
                      type: "pattern",
                      message: 'Digite a data correta, ex:. dd/mm/aaaa',
                    })
                  } else 
                  if(value && (mask === 'cep' || mask === 'cep-autocomplete') && value.length < 9){
                    onSetDisabledButtons(true);
                    setError(index.toString(), {
                      type: "pattern",
                      message: 'Digite o CEP corretamente.',
                    })
                  }
                }}
                {...propsInput}
              />
            </FormControl>
        )}}
      />
  )
}